/**
 * Single source of truth for which PassDesign image slots the editor offers per
 * Apple style. Mirrors the EXACT per-style predicates validatePassDesign enforces:
 *
 *   footer     → boardingPass only
 *   strip      → coupon | storeCard | eventTicket
 *   thumbnail  → generic | eventTicket
 *   background → eventTicket only
 *   icon       → required on every style
 *   logo       → optional, offered on every style
 *
 * Plus the eventTicket strip-XOR-(background|thumbnail) mutual exclusion. The
 * Images editor uses this so it never offers a slot the server would 422 on PUT.
 */

import type { AppleStyle, PassDesign } from '@insurwiz/wallet-core';

/** Every PassDesign.images key, in editor render order. */
export type ImageSlot = keyof PassDesign['images'];

export const IMAGE_SLOTS: readonly ImageSlot[] = [
  'icon',
  'logo',
  'strip',
  'thumbnail',
  'background',
  'footer',
] as const;

/** The slots offered for `style`, mirroring validatePassDesign's per-style image rules. */
export function slotsForStyle(style: AppleStyle): ImageSlot[] {
  return IMAGE_SLOTS.filter((slot) => isSlotShown(style, slot));
}

/** Whether `slot` is supported on `style` (the validatePassDesign per-style image predicate). */
export function isSlotShown(style: AppleStyle, slot: ImageSlot): boolean {
  switch (slot) {
    case 'icon':
    case 'logo':
      return true;
    case 'footer':
      return style === 'boardingPass';
    case 'strip':
      return style === 'coupon' || style === 'storeCard' || style === 'eventTicket';
    case 'thumbnail':
      return style === 'generic' || style === 'eventTicket';
    case 'background':
      return style === 'eventTicket';
    default:
      return false;
  }
}

/**
 * For eventTicket only: which currently-set sibling slots disable `slot`, so the
 * UI pre-empts the validatePassDesign "eventTicket cannot combine strip with
 * background or thumbnail" error. `present` flags which sibling slots currently
 * carry an s3KeyBase.
 */
export function eventTicketSlotDisabled(
  slot: ImageSlot,
  present: { strip?: boolean; background?: boolean; thumbnail?: boolean },
): boolean {
  if (slot === 'strip') return Boolean(present.background) || Boolean(present.thumbnail);
  if (slot === 'background' || slot === 'thumbnail') return Boolean(present.strip);
  return false;
}
